import { queryOptions } from "@tanstack/react-query";
import {
  fetchKpiSectionLayout,
  fetchUserKpis,
  type UserKpiScope,
} from "@/features/kpi-settings/api/user-kpis.api";

/**
 * Keyed on tenant alone, deliberately NOT on scope. The overlay has no page
 * column and resolves per (tenant, user), so `/overview/user-kpis` and
 * `/drilldown/user-kpis` return identical rows — the scope only selects which
 * PAGE permission gates the request. A scope-partitioned key would cache the
 * same payload twice and let a Drilldown drawer edit go unseen by the Overview
 * picker until its staleTime lapsed.
 */
export const userKpiKeys = {
  all: ["user-kpis"] as const,
  scope: (tenantId: string) => ["user-kpis", tenantId] as const,
  // Not nested under `scope`, so invalidating the KPI list does not refetch this.
  sections: (tenantId: string) => ["user-kpis", "sections", tenantId] as const,
};

/** Per-user KPI settings. A user in several orgs has an independent set in each. */
export function userKpisQueryOptions(scope: UserKpiScope, tenantId: string) {
  return queryOptions({
    queryKey: userKpiKeys.scope(tenantId),
    queryFn: () => fetchUserKpis(scope),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

/** Section order and removed sections. Keyed by tenant, same as the KPI list. */
export function kpiSectionLayoutQueryOptions(scope: UserKpiScope, tenantId: string) {
  return queryOptions({
    queryKey: userKpiKeys.sections(tenantId),
    queryFn: () => fetchKpiSectionLayout(scope),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

export type { UserKpiScope };
